/**
 * The respondent-facing "bounce" button: a small draggable FAB that jumps the
 * form back up — either to the very top, or to the heading of the block the
 * respondent is currently in (`components/fields/BounceButton.tsx`).
 *
 * It is a sibling of the floating section menu, not a part of it: a long
 * single-section form has nowhere to navigate to but still scrolls for metres,
 * so the bounce button is offered whether or not `useFloatingMenu` is on. Its
 * look and feel deliberately reuses the menu's vocabulary — spawn corner, edge
 * padding, colour — so the two buttons can be made to match (or contrast)
 * without a second set of concepts to learn; `FloatingMenuPosition` and the
 * colour helpers are imported from `lib/floating-menu.ts` rather than cloned.
 *
 * Where the *target* is decided: here, by the author, in the builder's "Bounce"
 * dialog. Tapping the button on the form performs that one jump — there is no
 * respondent-facing choice modal, because a respondent who has just scrolled
 * past the field they wanted wants one tap, not two.
 *
 * Like the menu style, this lives in the form-content envelope
 * (`lib/formContent.ts`) — no DB migration, and it rides the existing
 * `/api/forms/share` payload into the offline definition cache for free.
 * `serializeFormContent` drops it when it matches the defaults, so forms that
 * never opened the dialog keep byte-identical content and don't show as dirty.
 *
 * Kept free of React so the server payload builder and tests can import it; the
 * icon-name → component map lives in `components/fields/bounce-button-icons.tsx`.
 */
import {
  MAX_EDGE_PADDING,
  MIN_EDGE_PADDING,
  normalizeHexColor,
  FLOATING_MENU_POSITIONS,
  type FloatingMenuPosition,
} from "@/lib/floating-menu"

/** What a tap jumps to. */
export type BounceTarget = "top" | "heading"

export const BOUNCE_TARGETS: { value: BounceTarget; label: string; hint: string }[] = [
  {
    value: "top",
    label: "Bounce to the top",
    hint: "Jumps straight back to the top of the section being filled in.",
  },
  {
    value: "heading",
    label: "Bounce to the nearest heading",
    hint: "Jumps to the heading above the respondent; tapping again keeps stepping up.",
  },
]

/** Allow-listed lucide glyphs — free-text icon names would break the bundle. */
export type BounceIconName =
  | "arrow-up"
  | "arrow-up-to-line"
  | "chevrons-up"
  | "corner-left-up"
  | "rocket"

export const BOUNCE_ICON_NAMES: BounceIconName[] = [
  "arrow-up",
  "arrow-up-to-line",
  "chevrons-up",
  "corner-left-up",
  "rocket",
]

export type BounceButtonConfig = {
  /** Off by default: an unasked-for second FAB would crowd every existing form. */
  enabled: boolean
  target: BounceTarget
  /** Where the button starts. Respondents can still drag it anywhere. */
  position: FloatingMenuPosition
  /** Button background as `#rrggbb`; the glyph's colour derives from it. */
  color: string
  icon: BounceIconName
  /**
   * Gap in px between the button and the edge of the surface it lives on —
   * where it spawns, and how close a drag may push it back to the edge.
   */
  edgePadding: number
}

/**
 * Bottom-right, so the stock bounce button and the stock floating menu (top
 * right) don't spawn on top of each other on a form that switches both on.
 */
export const DEFAULT_BOUNCE_BUTTON: BounceButtonConfig = {
  enabled: false,
  target: "top",
  position: "bottom-right",
  color: "#4f46e5",
  icon: "arrow-up",
  edgePadding: 12,
}

/** Corner options are shared with the floating menu — one vocabulary, one list. */
export const BOUNCE_POSITIONS = FLOATING_MENU_POSITIONS

function clampPadding(raw: unknown): number {
  const n = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : NaN
  if (Number.isNaN(n)) return DEFAULT_BOUNCE_BUTTON.edgePadding
  return Math.min(MAX_EDGE_PADDING, Math.max(MIN_EDGE_PADDING, n))
}

/**
 * Lenient parse of the envelope's `bounceButton` key. Returns undefined when
 * absent/malformed so an untouched form serializes without the key; every
 * individual field falls back to its default, so a partial object is fine.
 */
export function parseBounceButton(raw: unknown): BounceButtonConfig | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const obj = raw as Record<string, unknown>
  const position = FLOATING_MENU_POSITIONS.some((p) => p.value === obj.position)
    ? (obj.position as FloatingMenuPosition)
    : DEFAULT_BOUNCE_BUTTON.position
  const target = BOUNCE_TARGETS.some((t) => t.value === obj.target)
    ? (obj.target as BounceTarget)
    : DEFAULT_BOUNCE_BUTTON.target
  const icon = BOUNCE_ICON_NAMES.includes(obj.icon as BounceIconName)
    ? (obj.icon as BounceIconName)
    : DEFAULT_BOUNCE_BUTTON.icon

  return {
    enabled: obj.enabled === true,
    target,
    position,
    color: normalizeHexColor(obj.color) ?? DEFAULT_BOUNCE_BUTTON.color,
    icon,
    edgePadding: clampPadding(obj.edgePadding),
  }
}

/** Fill in every unset field — the single place render code resolves a config. */
export function resolveBounceButton(
  config: BounceButtonConfig | null | undefined,
): BounceButtonConfig {
  if (!config) return DEFAULT_BOUNCE_BUTTON
  return {
    enabled: config.enabled === true,
    target: config.target ?? DEFAULT_BOUNCE_BUTTON.target,
    position: config.position ?? DEFAULT_BOUNCE_BUTTON.position,
    color: normalizeHexColor(config.color) ?? DEFAULT_BOUNCE_BUTTON.color,
    icon: config.icon ?? DEFAULT_BOUNCE_BUTTON.icon,
    edgePadding: clampPadding(config.edgePadding),
  }
}

/** True when nothing has been customised, so the key can be dropped on save. */
export function isDefaultBounceButton(config: BounceButtonConfig | undefined): boolean {
  if (!config) return true
  const resolved = resolveBounceButton(config)
  return (
    resolved.enabled === DEFAULT_BOUNCE_BUTTON.enabled &&
    resolved.target === DEFAULT_BOUNCE_BUTTON.target &&
    resolved.position === DEFAULT_BOUNCE_BUTTON.position &&
    resolved.color === DEFAULT_BOUNCE_BUTTON.color &&
    resolved.icon === DEFAULT_BOUNCE_BUTTON.icon &&
    resolved.edgePadding === DEFAULT_BOUNCE_BUTTON.edgePadding
  )
}
